import { PdfAnnotationObject, PdfBlendMode, Rect } from '@embedpdf/models';
import { TrackedAnnotation, PreviewState } from '@embedpdf/plugin-annotation';
import {
  HandleElementProps,
  SelectionMenuPropsBase,
  SelectionMenuRenderFn,
} from '@embedpdf/utils/@framework';
import { JSX, CSSProperties, MouseEvent } from '@framework';
import { VertexConfig } from '../types';

export type ResizeDirection = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'none';

export interface AnnotationSelectionContext {
  type: 'annotation';
  annotation: TrackedAnnotation;
  pageIndex: number;
  /**
   * Derived: PDF `locked` flag is set OR the annotation is non-interactive.
   * Consumers should disable move/resize/rotate/delete/property-change menu items
   * when this is true.
   */
  structurallyLocked: boolean;
  /**
   * Derived: PDF `lockedContents` flag is set OR the annotation is non-interactive.
   * Consumers should disable content-editing menu items (e.g. FreeText text edit)
   * when this is true.
   */
  contentLocked: boolean;
}

export type AnnotationSelectionMenuProps = SelectionMenuPropsBase<AnnotationSelectionContext>;
export type AnnotationSelectionMenuRenderFn = SelectionMenuRenderFn<AnnotationSelectionContext>;

export interface GroupSelectionContext {
  type: 'group';
  annotations: TrackedAnnotation[];
  pageIndex: number;
}

export type GroupSelectionMenuProps = SelectionMenuPropsBase<GroupSelectionContext>;
export type GroupSelectionMenuRenderFn = SelectionMenuRenderFn<GroupSelectionContext>;

export type HandleProps = HandleElementProps & {
  backgroundColor?: string;
};

/** UI customization for resize handles */
export interface ResizeHandleUI {
  /** Handle size in CSS px (default: 12) */
  size?: number;
  /** Default background color for the handle (used by default renderer) */
  color?: string;
  /** Custom renderer for each handle (overrides default) */
  component?: (p: HandleProps) => JSX.Element;
}

/** UI customization for vertex handles */
export interface VertexHandleUI {
  /** Handle size in CSS px (default: 12) */
  size?: number;
  /** Default background color for the handle (used by default renderer) */
  color?: string;
  /** Custom renderer for each vertex (overrides default) */
  component?: (p: HandleProps) => JSX.Element;
}

/** Props for the rotation handle component */
export interface RotationHandleComponentProps extends HandleProps {
  /** Props for the connector line element */
  connectorStyle?: CSSProperties;
  /** Whether to show the connector line */
  showConnector?: boolean;
  /** Color for the icon inside the handle (default: '#007ACC') */
  iconColor?: string;
  /** Resolved border configuration */
  border?: RotationHandleBorder;
}

export type BorderStyle = 'solid' | 'dashed' | 'dotted';

export interface SelectionOutline {
  /** Outline color (default: '#007ACC') */
  color?: string;
  /** Outline style (default: 'solid' for single, 'dashed' for group) */
  style?: BorderStyle;
  /** Outline width in px (default: 1 for single, 2 for group) */
  width?: number;
  /** Outline offset in px (default: 1 for single, 2 for group) */
  offset?: number;
}

/** Border configuration for the rotation handle */
export interface RotationHandleBorder {
  /** Border color (default: '#007ACC') */
  color?: string;
  /** Border style (default: 'solid') */
  style?: BorderStyle;
  /** Border width in px (default: 1) */
  width?: number;
}

/** UI customization for rotation handle */
export interface RotationHandleUI {
  /** Handle size in CSS px (default: 16) */
  size?: number;
  /** Gap in CSS px between the bounding box edge and the rotation handle center (default: 20) */
  margin?: number;
  /** Default background color for the handle (default: 'white') */
  color?: string;
  /** Color for the connector line (default: '#007ACC') */
  connectorColor?: string;
  /** Whether to show the connector line (default: true) */
  showConnector?: boolean;
  /** Color for the icon inside the handle (default: '#007ACC') */
  iconColor?: string;
  /** Border configuration for the handle */
  border?: RotationHandleBorder;
  /** Custom renderer for the rotation handle (overrides default) */
  component?: (p: RotationHandleComponentProps) => JSX.Element;
}

/**
 * Props for the custom annotation renderer
 */
export interface CustomAnnotationRendererProps<T extends PdfAnnotationObject> {
  annotation: T;
  children: JSX.Element;
  isSelected: boolean;
  scale: number;
  rotation: number;
  pageWidth: number;
  pageHeight: number;
  pageIndex: number;
  onSelect: (event: any) => void;
}

/**
 * Custom renderer for an annotation
 */
export type CustomAnnotationRenderer<T extends PdfAnnotationObject> = (
  props: CustomAnnotationRendererProps<T>,
) => JSX.Element | null;

/**
 * Properly typed event for annotation interactions (click, select, etc.)
 */
export type AnnotationInteractionEvent = MouseEvent<Element>;

/**
 * Props for an annotation renderer entry
 */
export interface AnnotationRendererProps<T extends PdfAnnotationObject = PdfAnnotationObject> {
  annotation: TrackedAnnotation<T>;
  currentObject: T;
  isSelected: boolean;
  isEditing: boolean;
  scale: number;
  pageIndex: number;
  documentId: string;
  onClick?: (e: AnnotationInteractionEvent) => void;
  /** When true, AP canvas provides the visual; component should only render hit area */
  appearanceActive: boolean;
}

/**
 * Helpers passed to selectOverride for custom selection behavior.
 */
export interface SelectOverrideHelpers {
  defaultSelect: (e: AnnotationInteractionEvent, annotation: TrackedAnnotation) => void;
  selectAnnotation: (pageIndex: number, id: string) => void;
  clearSelection: () => void;
  allAnnotations: TrackedAnnotation[];
  pageIndex: number;
}

/**
 * Entry for a custom annotation renderer that handles specific annotation types.
 * This allows external plugins to provide their own rendering for annotation subtypes.
 * Used at definition time for type safety.
 */
export interface AnnotationRendererEntry<
  T extends PdfAnnotationObject = PdfAnnotationObject,
  P = never,
> {
  /** Unique identifier for this renderer (usually matches tool id) */
  id: string;

  /** Returns true if this renderer should handle the annotation. Optional for preview-only renderers. */
  matches?: (annotation: PdfAnnotationObject) => annotation is T;

  /** The component to render the annotation. Optional for preview-only renderers. */
  render?: (props: AnnotationRendererProps<T>) => JSX.Element;

  /** Returns true if this renderer should handle the given preview state */
  matchesPreview?: (preview: PreviewState) => boolean;

  /** Render a preview during drag-to-create. P is the preview data type. */
  renderPreview?: (props: { data: P; bounds: Rect; scale: number }) => JSX.Element;

  /** Extra styles merged onto the preview container div (e.g. mixBlendMode for ink). */
  previewContainerStyle?: (props: { data: P; bounds: Rect; scale: number }) => CSSProperties;

  /** Vertex configuration for annotations with draggable vertices (line, polyline, polygon) */
  vertexConfig?: VertexConfig<T>;

  /** z-index for the annotation container (default: 1, text markup uses 0) */
  zIndex?: number;

  /** Default blend mode for this annotation type (used when annotation.blendMode is not set) */
  defaultBlendMode?: PdfBlendMode;

  /** Style applied to the annotation container — overrides the default blend-mode style. */
  containerStyle?: (annotation: T) => CSSProperties;

  /** Type-specific interaction fallbacks used when the tool doesn't define a property */
  interactionDefaults?: {
    isDraggable?: boolean;
    isResizable?: boolean;
    isRotatable?: boolean;
    lockAspectRatio?: boolean;
  };

  /** Whether this annotation type uses AP rendering before editing (default: true) */
  useAppearanceStream?: boolean;

  /** Override resolved isDraggable (e.g., FreeText disables drag while editing) */
  isDraggable?: (toolDraggable: boolean, context: { isEditing: boolean }) => boolean;

  /** Handle double-click on the annotation container */
  onDoubleClick?: (annotationId: string, setEditingId: (id: string) => void) => void;

  /** Override default selection behavior (e.g., Link IRT parent resolution) */
  selectOverride?: (
    e: AnnotationInteractionEvent,
    annotation: TrackedAnnotation<T>,
    helpers: SelectOverrideHelpers,
  ) => void;

  /** Return true to hide the selection menu for this annotation */
  hideSelectionMenu?: (annotation: T) => boolean;

  /** When true, the annotation is completely hidden when locked (e.g., form widgets defer to the form-filling layer). */
  hiddenWhenLocked?: boolean;

  /** Optional locked-mode renderer. When the annotation is locked, this replaces `render` inside the container. */
  renderLocked?: (props: AnnotationRendererProps<T>) => JSX.Element;
}

/**
 * Boxed renderer that encapsulates type safety internally.
 * The generic is erased -- this is what the registry actually stores.
 */
export interface BoxedAnnotationRenderer {
  id: string;
  matches: (annotation: PdfAnnotationObject) => boolean;
  render: (props: AnnotationRendererProps) => JSX.Element;
  matchesPreview?: (preview: PreviewState) => boolean;
  renderPreview?: (props: { data: unknown; bounds: Rect; scale: number }) => JSX.Element;
  previewContainerStyle?: (props: { data: unknown; bounds: Rect; scale: number }) => CSSProperties;
  vertexConfig?: VertexConfig<PdfAnnotationObject>;
  zIndex?: number;
  defaultBlendMode?: PdfBlendMode;
  /** Style applied to the annotation container — overrides the default blend-mode style. */
  containerStyle?: (annotation: PdfAnnotationObject) => CSSProperties;
  interactionDefaults?: {
    isDraggable?: boolean;
    isResizable?: boolean;
    isRotatable?: boolean;
    lockAspectRatio?: boolean;
  };
  useAppearanceStream?: boolean;
  isDraggable?: (toolDraggable: boolean, context: { isEditing: boolean }) => boolean;
  onDoubleClick?: (annotationId: string, setEditingId: (id: string) => void) => void;
  selectOverride?: (
    e: AnnotationInteractionEvent,
    annotation: TrackedAnnotation,
    helpers: SelectOverrideHelpers,
  ) => void;
  hideSelectionMenu?: (annotation: PdfAnnotationObject) => boolean;
  hiddenWhenLocked?: boolean;
  renderLocked?: (props: AnnotationRendererProps) => JSX.Element;
}

/**
 * Creates a boxed renderer from a typed entry.
 * Type safety is enforced at definition time, then erased for storage.
 */
export function createRenderer<T extends PdfAnnotationObject, P = never>(
  entry: AnnotationRendererEntry<T, P>,
): BoxedAnnotationRenderer {
  return {
    id: entry.id,
    matches: entry.matches ? (annotation) => entry.matches!(annotation) : () => false,
    render: entry.render
      ? (props) => entry.render!(props as AnnotationRendererProps<T>)
      : () => null as any,
    matchesPreview: entry.matchesPreview,
    renderPreview: entry.renderPreview
      ? (props) => entry.renderPreview!(props as { data: P; bounds: Rect; scale: number })
      : undefined,
    previewContainerStyle: entry.previewContainerStyle
      ? (props) => entry.previewContainerStyle!(props as { data: P; bounds: Rect; scale: number })
      : undefined,
    vertexConfig: entry.vertexConfig as VertexConfig<PdfAnnotationObject> | undefined,
    zIndex: entry.zIndex,
    defaultBlendMode: entry.defaultBlendMode,
    containerStyle: entry.containerStyle as
      | ((annotation: PdfAnnotationObject) => CSSProperties)
      | undefined,
    interactionDefaults: entry.interactionDefaults,
    useAppearanceStream: entry.useAppearanceStream,
    isDraggable: entry.isDraggable,
    onDoubleClick: entry.onDoubleClick,
    selectOverride: entry.selectOverride as BoxedAnnotationRenderer['selectOverride'],
    hideSelectionMenu: entry.hideSelectionMenu as
      | ((annotation: PdfAnnotationObject) => boolean)
      | undefined,
    hiddenWhenLocked: entry.hiddenWhenLocked,
    renderLocked: entry.renderLocked
      ? (props) => entry.renderLocked!(props as AnnotationRendererProps<T>)
      : undefined,
  };
}
